import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FolderClosed, Shield, Trash2, Download, ChevronDown, ChevronRight, FileText, Target, Sparkles, BookCheck } from 'lucide-react';
import { downloadAsDoc } from '../utils/exportDoc';

const C = {
  bg: '#0F0F0F',
  surf: '#161616',
  card: '#1C1C1C',
  bdr: '#2A2A2A',
  accent: '#D97757',
  accentS: 'rgba(217,119,87,0.10)',
  accentB: 'rgba(217,119,87,0.28)',
  green: '#4CAF82',
  greenS: 'rgba(76,175,130,0.12)',
  orange: '#F0A050',
  text: '#F0EDE8',
  muted: '#888',
  dim: '#2A2A2A',
  sidebar: '#111111',
  danger: '#FF7070',
};

const STAGES = ['Gap Analysis', 'Create Brief', 'First Draft', 'Make It Yours', 'Export'];

function StatusPill({ stage, done, analyzing }) {
  if (analyzing) return <span style={{ fontSize: 12, color: C.orange, fontFamily: 'JetBrains Mono, monospace' }}>Scanning...</span>;
  if (done.includes(5)) return <span style={{ fontSize: 12, color: C.green }}>✓ Done</span>;
  if (stage === 0 && !done.length) return <span style={{ fontSize: 12, color: C.muted }}>Ready</span>;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <div style={{ width: 5, height: 5, borderRadius: '50%', background: C.orange, animation: 'pulse 1.4s infinite' }} />
      <span style={{ fontSize: 12, color: C.orange, fontFamily: 'JetBrains Mono, monospace' }}>{STAGES[stage - 1] || 'Running'}</span>
    </div>
  );
}

function DeleteConfirmModal({ session, onConfirm, onCancel }) {
  return (
    <div
      data-testid="delete-confirm-modal"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.65)',
        backdropFilter: 'blur(4px)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        animation: 'fadeUp .15s ease',
      }}
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: C.card,
          border: `0.5px solid ${C.bdr}`,
          borderRadius: 12,
          padding: 24,
          maxWidth: 420,
          width: '90%',
          boxShadow: '0 24px 60px rgba(0,0,0,0.6)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <div style={{ width: 36, height: 36, borderRadius: 9, background: 'rgba(255,112,112,0.12)', border: `0.5px solid rgba(255,112,112,0.3)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Trash2 size={18} color={C.danger} strokeWidth={2} />
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 600, color: C.text }}>Delete this project?</div>
            <div style={{ fontSize: 13, color: C.muted, fontFamily: 'JetBrains Mono, monospace' }}>{session.url || 'unnamed session'}</div>
          </div>
        </div>
        <div style={{ fontSize: 14, color: C.muted, lineHeight: 1.6, marginBottom: 18 }}>
          This will permanently remove the gap analysis, brief, draft and humanised content for this project.
          <br />
          <strong style={{ color: C.text }}>This action cannot be undone.</strong>
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            onClick={onCancel}
            data-testid="delete-cancel-btn"
            style={{ padding: '7px 14px', borderRadius: 7, background: 'transparent', color: C.muted, border: `0.5px solid ${C.bdr}`, cursor: 'pointer', fontSize: 13.5 }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            data-testid="delete-confirm-btn"
            style={{ padding: '7px 14px', borderRadius: 7, background: C.danger, color: '#fff', border: 'none', cursor: 'pointer', fontSize: 13.5, fontWeight: 500 }}
          >
            Delete permanently
          </button>
        </div>
      </div>
    </div>
  );
}

function ProjectStats({ session, onDownloadCurrent, onDownloadArticle, hasContent }) {
  const topicsCount = session.topics?.length || 0;
  const wordCount = (session.pol || session.draft || '').trim().split(/\s+/).filter(Boolean).length;
  const stagesDone = session.done?.length || 0;
  const articles = session.completed_articles || [];

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        marginTop: 8,
        padding: '10px 11px',
        background: C.surf,
        border: `0.5px solid ${C.bdr}`,
        borderRadius: 7,
        animation: 'fadeUp .2s ease',
      }}
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 6, marginBottom: (hasContent || articles.length > 0) ? 9 : 0 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
            <Target size={10} color={C.accent} strokeWidth={2} />
            <span style={{ fontSize: 10, color: C.muted, fontFamily: 'JetBrains Mono, monospace', textTransform: 'uppercase', letterSpacing: 0.4 }}>Gaps</span>
          </div>
          <div style={{ fontSize: 16, fontWeight: 600, color: C.text, lineHeight: 1 }}>{topicsCount}</div>
        </div>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
            <FileText size={10} color={C.accent} strokeWidth={2} />
            <span style={{ fontSize: 10, color: C.muted, fontFamily: 'JetBrains Mono, monospace', textTransform: 'uppercase', letterSpacing: 0.4 }}>Words</span>
          </div>
          <div style={{ fontSize: 16, fontWeight: 600, color: C.text, lineHeight: 1 }}>{wordCount || '—'}</div>
        </div>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
            <Sparkles size={10} color={C.accent} strokeWidth={2} />
            <span style={{ fontSize: 10, color: C.muted, fontFamily: 'JetBrains Mono, monospace', textTransform: 'uppercase', letterSpacing: 0.4 }}>Stages</span>
          </div>
          <div style={{ fontSize: 16, fontWeight: 600, color: C.text, lineHeight: 1 }}>{stagesDone}<span style={{ fontSize: 11, color: C.muted, fontWeight: 400 }}>/5</span></div>
        </div>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
            <BookCheck size={10} color={C.green} strokeWidth={2} />
            <span style={{ fontSize: 10, color: C.green, fontFamily: 'JetBrains Mono, monospace', textTransform: 'uppercase', letterSpacing: 0.4 }}>Done</span>
          </div>
          <div style={{ fontSize: 16, fontWeight: 600, color: C.text, lineHeight: 1 }}>{articles.length}</div>
        </div>
      </div>

      {/* Articles list */}
      {articles.length > 0 && (
        <div data-testid="articles-list" style={{ marginBottom: hasContent ? 9 : 0 }}>
          <div style={{ fontSize: 10, color: C.muted, fontFamily: 'JetBrains Mono, monospace', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 5 }}>
            Completed articles
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 160, overflowY: 'auto' }}>
            {articles.map((a, i) => (
              <div
                key={a.id || i}
                data-testid={`completed-article-${i}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '5px 7px',
                  background: C.card,
                  border: `0.5px solid ${C.bdr}`,
                  borderRadius: 5,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, color: C.text, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.3 }} title={a.title}>
                    {a.title}
                  </div>
                  {a.keyword && (
                    <div style={{ fontSize: 10, color: C.muted, fontFamily: 'JetBrains Mono, monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={a.keyword}>
                      {a.keyword}
                    </div>
                  )}
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDownloadArticle(a);
                  }}
                  title="Download .doc"
                  data-testid={`article-download-btn-${i}`}
                  style={{
                    background: 'transparent',
                    border: `0.5px solid ${C.bdr}`,
                    borderRadius: 4,
                    color: C.accent,
                    cursor: 'pointer',
                    padding: '3px 5px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    transition: 'all .15s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = C.accent;
                    e.currentTarget.style.background = C.accentS;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = C.bdr;
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <Download size={10} strokeWidth={2.2} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {hasContent && (
        <button
          onClick={onDownloadCurrent}
          data-testid="sidebar-download-doc-btn"
          style={{
            width: '100%',
            padding: '6px 8px',
            background: C.accentS,
            border: `0.5px solid ${C.accentB}`,
            borderRadius: 6,
            color: C.accent,
            fontSize: 12,
            fontWeight: 500,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 5,
            transition: 'all .15s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = C.accentB;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = C.accentS;
          }}
        >
          <Download size={12} strokeWidth={2} /> Download current draft
        </button>
      )}
    </div>
  );
}

export default function Sidebar({ sessions, activeId, analyzing, user, onSelect, onAdd, onRemove, onLogout, collapsed, onToggle, showToast }) {
  const navigate = useNavigate();
  const [adding, setAdding] = useState(false);
  const [newUrl, setNewUrl] = useState('');
  const [newInd, setNewInd] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null); // session object pending delete
  const [expanded, setExpanded] = useState({}); // { [sessionId]: true }

  const submit = () => {
    if (!newUrl.trim()) return;
    onAdd(newUrl.trim(), newInd.trim());
    setNewUrl('');
    setNewInd('');
    setAdding(false);
  };

  const handleDownloadCurrent = (s) => {
    const content = s.pol || s.draft;
    if (!content) {
      showToast?.('Nothing to download yet');
      return;
    }
    downloadAsDoc(content, s.topic?.title || s.url || 'Blog Post');
    showToast?.('Word doc downloaded!');
  };

  const handleDownloadArticle = (article) => {
    downloadAsDoc(article.content, article.title || 'Blog Post');
    showToast?.('Word doc downloaded!');
  };

  const toggleExpanded = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const w = collapsed ? 52 : 264;

  return (
    <div style={{ width: w, minWidth: w, height: '100vh', background: C.sidebar, borderRight: `1px solid ${C.bdr}`, display: 'flex', flexDirection: 'column', transition: 'width .2s, min-width .2s', overflow: 'hidden', flexShrink: 0, zIndex: 10 }}>
      {/* Brand header: ContentGap logo */}
      <div style={{ height: 54, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: collapsed ? '0 11px' : '0 14px', borderBottom: `1px solid ${C.bdr}`, flexShrink: 0 }}>
        {!collapsed && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 26, height: 26, borderRadius: 6, background: C.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: '#fff' }}>C</div>
            <span style={{ fontSize: 16, fontWeight: 600, letterSpacing: '-.3px' }} data-testid="brand-name">
              Content<span style={{ color: C.accent }}>Gap</span>
            </span>
            <span style={{ fontSize: 10, color: C.muted, fontFamily: 'JetBrains Mono, monospace', background: C.surf, border: `0.5px solid ${C.bdr}`, padding: '2px 6px', borderRadius: 4 }}>live</span>
          </div>
        )}
        <button onClick={onToggle} style={{ background: 'transparent', border: `0.5px solid ${C.bdr}`, borderRadius: 6, color: C.muted, cursor: 'pointer', padding: '5px 7px', fontSize: 14 }}>
          {collapsed ? '>' : '<'}
        </button>
      </div>

      {!collapsed && (
        <>
          {/* Sessions subheader + Add URL button */}
          <div style={{ padding: '14px 12px 10px', borderBottom: `1px solid ${C.bdr}`, flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 10, padding: '0 2px' }}>
              <FolderClosed size={14} color={C.muted} strokeWidth={1.8} />
              <span style={{ fontSize: 12, fontWeight: 500, color: C.muted, textTransform: 'uppercase', letterSpacing: '.6px' }} data-testid="sessions-subheader">Sessions</span>
              <span style={{ fontSize: 11, color: C.muted, background: C.card, border: `0.5px solid ${C.bdr}`, padding: '1px 6px', borderRadius: 3, fontFamily: 'JetBrains Mono, monospace' }}>{sessions.length}</span>
            </div>
            {adding ? (
              <div style={{ animation: 'fadeUp .2s ease' }}>
                <input
                  autoFocus
                  value={newUrl}
                  onChange={(e) => setNewUrl(e.target.value)}
                  placeholder="yourdomain.com"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') submit();
                    if (e.key === 'Escape') setAdding(false);
                  }}
                  style={{ width: '100%', padding: '7px 10px', background: C.card, border: `1px solid ${C.accent}`, borderRadius: 6, color: C.text, fontSize: 14, outline: 'none', marginBottom: 6 }}
                />
                <input
                  value={newInd}
                  onChange={(e) => setNewInd(e.target.value)}
                  placeholder="Industry / niche (optional)"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') submit();
                    if (e.key === 'Escape') setAdding(false);
                  }}
                  style={{ width: '100%', padding: '7px 10px', background: C.card, border: `0.5px solid ${C.bdr}`, borderRadius: 6, color: C.text, fontSize: 14, outline: 'none', marginBottom: 8 }}
                />
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={submit} style={{ flex: 1, padding: '6px', background: C.accent, color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
                    Add
                  </button>
                  <button
                    onClick={() => {
                      setAdding(false);
                      setNewUrl('');
                      setNewInd('');
                    }}
                    style={{ flex: 1, padding: '6px', background: 'transparent', color: C.muted, border: `0.5px solid ${C.bdr}`, borderRadius: 6, fontSize: 13, cursor: 'pointer' }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setAdding(true)}
                data-testid="sidebar-add-url-btn"
                style={{
                  width: '100%',
                  padding: '7px',
                  background: C.accentS,
                  border: `0.5px dashed ${C.accent}`,
                  borderRadius: 7,
                  color: C.accent,
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 5,
                }}
              >
                <span style={{ fontSize: 16 }}>+</span> Add URL
              </button>
            )}
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
            {sessions.map((s, idx) => {
              const isActive = s.id === activeId;
              const isAnalyzing = analyzing === s.id;
              const hasAnyData = s.topics?.length > 0 || s.brief || s.draft || s.pol || s.completed_articles?.length > 0;
              const hasContent = s.pol || s.draft;
              const isExpanded = expanded[s.id];

              return (
                <div
                  key={s.id}
                  data-testid={`session-card-${idx}`}
                  onClick={() => onSelect(s.id)}
                  style={{
                    margin: '0 8px 4px',
                    borderRadius: 8,
                    padding: '10px 10px 8px',
                    background: isActive ? C.accentS : 'transparent',
                    border: `0.5px solid ${isActive ? C.accentB : 'transparent'}`,
                    cursor: 'pointer',
                    transition: 'all .15s',
                    animation: 'slideIn .2s ease',
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) e.currentTarget.style.background = C.card;
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 6 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 3 }}>
                        <span
                          style={{
                            fontSize: 11,
                            fontFamily: 'JetBrains Mono, monospace',
                            fontWeight: 500,
                            color: isActive ? C.accent : C.muted,
                            background: isActive ? C.accentB : C.dim,
                            padding: '1px 5px',
                            borderRadius: 3,
                            flexShrink: 0,
                          }}
                        >
                          #{String(idx + 1).padStart(2, '0')}
                        </span>
                        <span style={{ fontSize: 14, fontWeight: 500, color: isActive ? C.text : '#AAA', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontStyle: s.url ? 'normal' : 'italic', opacity: s.url ? 1 : 0.55 }}>{s.url || 'yourdomain.com'}</span>
                      </div>
                      {s.ind && <div style={{ fontSize: 12, color: C.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 4 }}>{s.ind}</div>}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                        <StatusPill stage={s.stage} done={s.done} analyzing={isAnalyzing} />
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                          {s.completed_articles?.length > 0 && (
                            <span
                              data-testid={`articles-badge-${idx}`}
                              title={`${s.completed_articles.length} article${s.completed_articles.length !== 1 ? 's' : ''} written`}
                              style={{
                                fontSize: 11,
                                color: C.green,
                                fontFamily: 'JetBrains Mono, monospace',
                                background: C.greenS,
                                border: `0.5px solid rgba(76,175,130,0.28)`,
                                padding: '1px 5px',
                                borderRadius: 3,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 3,
                              }}
                            >
                              <BookCheck size={9} strokeWidth={2.4} /> {s.completed_articles.length}
                            </span>
                          )}
                          {s.topics && <span style={{ fontSize: 11, color: C.muted, fontFamily: 'JetBrains Mono, monospace' }}>{s.topics.length} gaps</span>}
                        </div>
                      </div>
                      {s.stage > 0 && (
                        <div style={{ marginTop: 6, display: 'flex', gap: 2 }}>
                          {STAGES.map((_, i) => (
                            <div
                              key={i}
                              style={{
                                flex: 1,
                                height: 2,
                                borderRadius: 2,
                                background: s.done.includes(i + 1) ? C.green : s.stage === i + 1 ? C.orange : C.bdr,
                                transition: 'background .3s',
                              }}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, flexShrink: 0 }}>
                      {hasAnyData && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleExpanded(s.id);
                          }}
                          title={isExpanded ? 'Hide stats' : 'Show stats'}
                          data-testid={`session-expand-btn-${idx}`}
                          style={{ background: 'transparent', border: `0.5px solid ${C.bdr}`, borderRadius: 4, color: C.muted, cursor: 'pointer', padding: '3px 4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                          {isExpanded ? <ChevronDown size={11} strokeWidth={2} /> : <ChevronRight size={11} strokeWidth={2} />}
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setConfirmDelete(s);
                        }}
                        title="Delete project"
                        data-testid={`session-delete-btn-${idx}`}
                        style={{ background: 'transparent', border: `0.5px solid ${C.bdr}`, borderRadius: 4, color: C.muted, cursor: 'pointer', padding: '3px 4px', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all .15s' }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = C.danger;
                          e.currentTarget.style.color = C.danger;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = C.bdr;
                          e.currentTarget.style.color = C.muted;
                        }}
                      >
                        <Trash2 size={11} strokeWidth={2} />
                      </button>
                    </div>
                  </div>
                  {isExpanded && hasAnyData && (
                    <ProjectStats
                      session={s}
                      hasContent={!!hasContent}
                      onDownloadCurrent={() => handleDownloadCurrent(s)}
                      onDownloadArticle={handleDownloadArticle}
                    />
                  )}
                </div>
              );
            })}
          </div>

          {/* User info + Logout */}
          <div style={{ padding: '12px 14px', borderTop: `1px solid ${C.bdr}`, flexShrink: 0 }}>
            {user && (
              <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    background: C.accent,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 15,
                    fontWeight: 600,
                    color: '#fff',
                    flexShrink: 0,
                    backgroundImage: user.picture ? `url(${user.picture})` : 'none',
                    backgroundSize: 'cover',
                  }}
                  data-testid="user-avatar"
                >
                  {!user.picture && (user.name || user.email || '?').charAt(0).toUpperCase()}
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div
                    style={{ fontSize: 15, fontWeight: 500, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 5 }}
                    data-testid="user-name"
                    title={user.name || user.email}
                  >
                    {user.name || user.email?.split('@')[0]}
                    {user.is_admin && <Shield size={11} color={C.accent} strokeWidth={2.2} />}
                  </div>
                  <div
                    style={{ fontSize: 13, color: C.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    data-testid="user-email"
                    title={user.email}
                  >
                    {user.email}
                  </div>
                </div>
              </div>
            )}
            {user?.is_admin && (
              <button
                onClick={() => navigate('/admin')}
                title="Admin console"
                data-testid="admin-link-btn"
                style={{
                  width: '100%',
                  background: C.accentS,
                  border: `0.5px solid ${C.accentB}`,
                  borderRadius: 6,
                  color: C.accent,
                  cursor: 'pointer',
                  padding: '8px',
                  fontSize: 13,
                  fontWeight: 500,
                  marginBottom: 7,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  transition: 'all .15s',
                }}
              >
                <Shield size={13} strokeWidth={2} /> Admin Console
              </button>
            )}
            <button
              onClick={onLogout}
              title="Log out"
              data-testid="logout-btn"
              style={{
                width: '100%',
                background: 'transparent',
                border: `0.5px solid ${C.bdr}`,
                borderRadius: 6,
                color: C.muted,
                cursor: 'pointer',
                padding: '9px',
                fontSize: 14,
                transition: 'all .15s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#FF7070';
                e.currentTarget.style.color = '#FF7070';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = C.bdr;
                e.currentTarget.style.color = C.muted;
              }}
            >
              Log out
            </button>
          </div>
        </>
      )}

      {collapsed && (
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
          {sessions.map((s, idx) => (
            <div
              key={s.id}
              onClick={() => onSelect(s.id)}
              title={s.url || `Session ${idx + 1}`}
              style={{
                width: 30,
                height: 30,
                borderRadius: 7,
                cursor: 'pointer',
                background: s.id === activeId ? C.accentS : C.card,
                border: `0.5px solid ${s.id === activeId ? C.accentB : C.bdr}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 12,
                fontWeight: 600,
                color: s.id === activeId ? C.accent : C.muted,
                fontFamily: 'JetBrains Mono, monospace',
              }}
            >
              {idx + 1}
            </div>
          ))}
          <button
            onClick={onToggle}
            style={{
              width: 30,
              height: 30,
              borderRadius: 7,
              background: 'transparent',
              border: `0.5px dashed ${C.accent}`,
              color: C.accent,
              cursor: 'pointer',
              fontSize: 18,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            +
          </button>
        </div>
      )}

      {confirmDelete && (
        <DeleteConfirmModal
          session={confirmDelete}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => {
            onRemove(confirmDelete.id);
            setConfirmDelete(null);
            showToast?.('Project deleted');
          }}
        />
      )}
    </div>
  );
}
